(function () {
  const tab = document.getElementById('tbh-phone-popup-tab');
  const popup = document.getElementById('tbh-pp');
  if (!(tab instanceof HTMLButtonElement) || !(popup instanceof HTMLElement)) return;

  const shopifyFormId = tab.dataset.shopifyFormId?.trim();
  if (!shopifyFormId) return;

  const POLL_INTERVAL_MS = 150;
  const FIELD_TIMEOUT_MS = 8000;
  const SUPPRESS_KEY = 'tbh-phone-popup-done';
  const SUBMITTED_KEY = 'tbh-phone-popup-submitted';
  const SEEN_KEY = 'tbh-phone-popup-seen';

  const autoOpen = popup.dataset.autoOpen === 'true';
  const autoOpenDelayMs = Math.max(0, Number(popup.dataset.autoOpenDelay || 5)) * 1000;
  const autoOpenFrequency = popup.dataset.autoOpenFrequency || 'session';
  const modernHostSelector = `shopify-forms-embed#app-embed-container-${CSS.escape(shopifyFormId)}`;
  const legacyRootSelector = `[data-forms-id="forms-root-${CSS.escape(shopifyFormId)}"]`;

  const form = popup.querySelector('.tbh-pp__form');
  const input = popup.querySelector('.tbh-pp__input');
  const cta = popup.querySelector('.tbh-pp__cta');
  const errorNote = popup.querySelector('[data-tbh-pp-error]');
  const successNote = popup.querySelector('[data-tbh-pp-success]');
  let lastFocused = null;
  let submitting = false;

  /* ── Shopify Forms embed plumbing ─────────────────────────────────────────
     The embed renders inside an open shadow root, so we walk every root to
     find its field and submit button. It stays visually hidden throughout. */

  function collectRoots(root = document, roots = []) {
    roots.push(root);
    root.querySelectorAll('*').forEach((element) => {
      if (element.shadowRoot) collectRoots(element.shadowRoot, roots);
    });
    return roots;
  }

  function findModernEmbed() {
    const host = document.querySelector(modernHostSelector);
    const embed = host?.shadowRoot?.querySelector('#app-embed[data-form-root="true"]');
    return embed instanceof HTMLElement ? embed : null;
  }

  function findLegacyFormRoot() {
    for (const root of collectRoots()) {
      const formRoot = root.querySelector(legacyRootSelector);
      if (formRoot) return formRoot;
    }
    return null;
  }

  function embedRoots() {
    const modernEmbed = findModernEmbed();
    if (modernEmbed) return collectRoots(modernEmbed);

    const legacyRoot = findLegacyFormRoot();
    return legacyRoot ? collectRoots(legacyRoot) : [];
  }

  function queryEmbed(selector) {
    for (const root of embedRoots()) {
      const match = root.querySelector(selector);
      if (match) return match;
    }
    return null;
  }

  /* The embed is parked off-screen by CSS from first paint (see tbh-base.css);
     these just toggle the escape-hatch class for the fallback path. */
  function hideNativeUi() {
    document.body.classList.remove('tbh-forms-native-visible');
  }

  function revealNativeUi() {
    document.body.classList.add('tbh-forms-native-visible');
  }

  function nativeLauncher() {
    const modernEmbed = findModernEmbed();
    if (modernEmbed) {
      const launcher = modernEmbed.querySelector('button[aria-label], button');
      return launcher instanceof HTMLElement ? launcher : null;
    }

    const teaserHost = queryEmbed('form-teaser, [data-testid*="teaser"]');
    if (teaserHost instanceof HTMLElement) {
      const launcher = teaserHost.shadowRoot?.querySelector('button, [role="button"]');
      return launcher instanceof HTMLElement ? launcher : teaserHost;
    }

    return queryEmbed('button[aria-haspopup="dialog"], [role="button"][aria-haspopup="dialog"]');
  }

  function nativePhoneField() {
    return queryEmbed('input[type="tel"], input[name*="phone" i], input[autocomplete*="tel" i]');
  }

  function nativeSubmitButton() {
    return queryEmbed('button[type="submit"], input[type="submit"], form button');
  }

  function waitFor(getter, timeoutMs) {
    return new Promise((resolve) => {
      const startedAt = Date.now();
      const timer = window.setInterval(() => {
        const value = getter();
        if (value || Date.now() - startedAt >= timeoutMs) {
          window.clearInterval(timer);
          resolve(value || null);
        }
      }, POLL_INTERVAL_MS);
    });
  }

  /* Framework-controlled inputs ignore `.value = x`, so go through the native
     setter and fire the events their state handlers listen for. */
  function setFieldValue(field, value) {
    const prototype = field instanceof HTMLTextAreaElement ? HTMLTextAreaElement : HTMLInputElement;
    const setter = Object.getOwnPropertyDescriptor(prototype.prototype, 'value')?.set;
    if (setter) {
      setter.call(field, value);
    } else {
      field.value = value;
    }

    field.dispatchEvent(new Event('input', { bubbles: true }));
    field.dispatchEvent(new Event('change', { bubbles: true }));
  }

  async function forwardToShopifyForm(phone) {
    hideNativeUi();

    const launcher = await waitFor(nativeLauncher, FIELD_TIMEOUT_MS);
    if (launcher) launcher.click();

    const field = await waitFor(nativePhoneField, FIELD_TIMEOUT_MS);
    if (!field) return false;

    setFieldValue(field, phone);

    const submit = nativeSubmitButton();
    if (!(submit instanceof HTMLElement)) return false;

    submit.click();
    return true;
  }

  /* ── Our popup ────────────────────────────────────────────────────────────── */

  function setError(message) {
    if (!errorNote) return;
    errorNote.textContent = message;
    errorNote.hidden = !message;
  }

  function openPopup() {
    if (popup.hidden === false) return;

    lastFocused = document.activeElement;
    popup.hidden = false;
    tab.hidden = true;
    tab.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    hideNativeUi();
    window.setTimeout(() => input?.focus(), 50);
  }

  function closePopup() {
    if (popup.hidden) return;

    popup.hidden = true;
    document.body.style.overflow = '';
    tab.hidden = safeGet(window.sessionStorage, SUPPRESS_KEY) === '1';
    tab.setAttribute('aria-expanded', 'false');
    if (lastFocused instanceof HTMLElement) lastFocused.focus();
  }

  tab.addEventListener('click', openPopup);

  popup.querySelectorAll('[data-tbh-pp-close]').forEach((closer) => {
    closer.addEventListener('click', closePopup);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !popup.hidden) closePopup();
  });

  form?.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (submitting) return;

    const phone = (input?.value || '').replace(/\D/g, '');
    if (phone.length !== 10) {
      setError('Enter a 10-digit mobile number.');
      input?.focus();
      return;
    }

    submitting = true;
    setError('');
    if (cta instanceof HTMLButtonElement) cta.disabled = true;

    let sent = false;
    try {
      sent = await forwardToShopifyForm(phone);
    } catch (error) {
      sent = false;
    }

    if (cta instanceof HTMLButtonElement) cta.disabled = false;
    submitting = false;

    if (!sent) {
      // Last resort: hand the shopper the stock form rather than lose the signup.
      setError('Something went wrong — opening the signup form.');
      revealNativeUi();
      window.setTimeout(closePopup, 600);
      return;
    }

    // Hide the inputs, not the form — the success note lives inside it.
    popup.querySelector('.tbh-pp__field')?.setAttribute('hidden', '');
    cta?.setAttribute('hidden', '');
    if (successNote instanceof HTMLElement) {
      successNote.textContent = successNote.dataset.message || 'You’re in.';
      successNote.hidden = false;
    }

    window.sessionStorage.setItem(SUPPRESS_KEY, '1');
    safeSet(window.localStorage, SUBMITTED_KEY, '1');
    window.setTimeout(closePopup, 2600);
  });

  /* ── Auto-open ────────────────────────────────────────────────────────────
     Skipped once someone has signed up, and in the theme editor, where a
     popup reopening on every render makes the section unworkable. */

  function safeGet(store, key) {
    try {
      return store.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function safeSet(store, key, value) {
    try {
      store.setItem(key, value);
    } catch (error) {
      /* private browsing — treat as "not seen" rather than breaking the popup */
    }
  }

  function shouldAutoOpen() {
    if (!autoOpen) return false;
    if (window.Shopify?.designMode) return false;
    if (safeGet(window.localStorage, SUBMITTED_KEY) === '1') return false;

    if (autoOpenFrequency === 'session') {
      return safeGet(window.sessionStorage, SEEN_KEY) !== '1';
    }

    if (autoOpenFrequency === 'daily') {
      const lastSeen = Number(safeGet(window.localStorage, SEEN_KEY) || 0);
      return Date.now() - lastSeen > 24 * 60 * 60 * 1000;
    }

    return true;
  }

  function markSeen() {
    if (autoOpenFrequency === 'daily') {
      safeSet(window.localStorage, SEEN_KEY, String(Date.now()));
      return;
    }

    safeSet(window.sessionStorage, SEEN_KEY, '1');
  }

  if (shouldAutoOpen()) {
    window.setTimeout(() => {
      if (popup.hidden) {
        markSeen();
        openPopup();
      }
    }, autoOpenDelayMs);
  }

  hideNativeUi();
  tab.hidden = safeGet(window.sessionStorage, SUPPRESS_KEY) === '1';
})();
